import { api } from './client';
import type {
  AgreementDocument,
  AgreementDocumentCreateInput,
  AgreementDocumentUpdateInput,
  Page,
  WriteResult,
} from './types';
import { UUID } from './types';

async function writeWithMaker<T>(
  call: () => Promise<{ data: T; status: number; headers: Headers }>,
): Promise<WriteResult<T>> {
  const r = await call();
  return {
    entity: r.data,
    pending: r.headers.get('X-Change-Request-Status') === 'pending',
    changeRequestId: r.headers.get('X-Change-Request-Id'),
  };
}

export const agreementDocumentsApi = {
  async create(input: AgreementDocumentCreateInput): Promise<WriteResult<AgreementDocument>> {
    return writeWithMaker(() => api.postMeta('/agreement-documents', input));
  },

  async list(
    filters?: { agreement_id?: UUID; include_deleted?: boolean },
    limit = 100,
    offset = 0,
  ): Promise<Page<AgreementDocument>> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.agreement_id) params.append('agreement_id', filters.agreement_id);
    if (filters?.include_deleted) params.append('include_deleted', 'true');
    return api.get(`/agreement-documents?${params}`);
  },

  async get(id: UUID): Promise<AgreementDocument> {
    return api.get(`/agreement-documents/${id}`);
  },

  async update(
    id: UUID,
    input: AgreementDocumentUpdateInput,
  ): Promise<WriteResult<AgreementDocument>> {
    return writeWithMaker(() => api.patchMeta(`/agreement-documents/${id}`, input));
  },

  async delete(id: UUID): Promise<WriteResult<AgreementDocument>> {
    return writeWithMaker(() => api.deleteMeta(`/agreement-documents/${id}`));
  },

  async restore(id: UUID): Promise<WriteResult<AgreementDocument>> {
    return writeWithMaker(() => api.postMeta(`/agreement-documents/${id}/restore`));
  },
};
